/**
 * Language Configuration Constants
 * 
 * Shared language configuration used across the deck visualizer frontend.
 * This mirrors the Python config.py SUPPORTED_LANGUAGES structure.
 */

// Supported languages with their metadata
export const SUPPORTED_LANGUAGES = {
  EN: {
    code: 'EN',
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸',
    isDefault: true,
  },
  JP: {
    code: 'JP',
    name: 'Japanese',
    nativeName: '日本語',
    flag: '🇯🇵',
    isDefault: false,
  },
  'ZH-HANT': {
    code: 'ZH-HANT',
    name: 'Chinese (Traditional)', // 繁體中文 — used in Taiwan, Hong Kong, Macau
    nativeName: '繁體中文',
    flag: '🇹🇼',
    isDefault: false,
  },
  KR: {
    code: 'KR',
    name: 'Korean',
    nativeName: '한국어',
    flag: '🇰🇷',
    isDefault: false,
  },
  // Future languages can be added here:
  // 'ZH-HANS': { code: 'ZH-HANS', name: 'Chinese (Simplified)', nativeName: '简体中文', flag: '🇨🇳', isDefault: false },
};

// Default language code
export const DEFAULT_LANGUAGE = 'EN';

// Get array of supported language codes
export const getSupportedLanguageCodes = () => Object.keys(SUPPORTED_LANGUAGES);

// Get language config by code
export const getLanguageConfig = (code) => {
  const upperCode = (code || DEFAULT_LANGUAGE).toUpperCase();
  return SUPPORTED_LANGUAGES[upperCode] || SUPPORTED_LANGUAGES[DEFAULT_LANGUAGE];
};

// Get display name for language (with native name)
export const getLanguageDisplayName = (code, includeNative = true) => {
  const config = getLanguageConfig(code);
  if (includeNative && config.name !== config.nativeName) {
    return `${config.name} (${config.nativeName})`;
  }
  return config.name;
};

// Get all languages as array for dropdowns/selectors
export const getLanguageOptions = () => {
  return Object.values(SUPPORTED_LANGUAGES).map(lang => ({
    value: lang.code,
    label: lang.name,
    nativeLabel: lang.nativeName,
    flag: lang.flag,
  }));
};

// Language storage key for localStorage
export const LANGUAGE_STORAGE_KEY = 'ultraman-deck-language';

// Get saved language from localStorage or return default
export const getSavedLanguage = () => {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  
  const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (saved && SUPPORTED_LANGUAGES[saved.toUpperCase()]) {
    return saved.toUpperCase();
  }
  return DEFAULT_LANGUAGE;
};

// Save language to localStorage
export const saveLanguage = (code) => {
  if (typeof window === 'undefined') return;
  
  const upperCode = (code || DEFAULT_LANGUAGE).toUpperCase();
  if (SUPPORTED_LANGUAGES[upperCode]) {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, upperCode);
  }
};
